import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';

import InputWorkingZipcodeForChargingMap from '../../../../components/InputComponents/InputZipcode/InputZipcodeForChargingMap';
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner';

import '../../../../components/LocationMap/LocationMap.scss';
import { FormattedMessage } from 'react-intl';
import useChargingStations from '../../../../hooks/useChargingStations';
import filterWithinBounds from '../../../../utils/Helpers/filterWithinBounds';
import BaseGoogleMapReact from '../../../../components/LocationMap/BaseGoogleMapReact/BaseGoogleMapReact';
import useMappedClusters from '../../../../hooks/useMappedClusters';
import ChargingStationsMapMarker from '../../../../components/LocationMap/ChargingStationsMapMarker/ChargingStationsMapMarker';
import ChargingStationClusterMarker from '../../../../components/LocationMap/ChargingStationClusterMarker/ChargingStationClusterMarker';
import isSuperChargingStation from '../../../../utils/predicates/isSuperChargingStation';
import useSelectedStation from '../../../../hooks/useSelectedStation';
import MapControlPanel from '../../../../components/LocationMap/MapControlPanel/MapControlPanel';
import UserPrefsContext from '../../../../context/UserPrefs/UserPrefsContext';


import './ChargingMap.scss';
import fullscreenIcon from '../../../assets/images/icons/fullscreen-icon.svg';
import mapControlsToggleIcon from '../../../assets/images/icons/map-control-toggle-icon.svg';

const DEFAULT_MAP_ZIPCODE = 23219;
const ChargingMap = ({
  userLocation,
  ignoreSuperchargerStations,
  isVisible = true,
  toggleWithHistory,
  isPage,
  additionalFetch,
  additionalBounds,
}) => {
  const userPrefs = useContext(UserPrefsContext);
  const [bounds, setBounds] = useState({
    nw: undefined,
    ne: undefined,
    sw: undefined,
    se: undefined,
  });

  const [center, setCenter] = useState();
  const [zoom, setZoom] = useState(10);
  const [hasRezoomed, setHasRezoomed] = useState(false);
  const [showMapControls, setShowMapControls] = useState(false);
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const isTogglingFullscreen = useRef(false);

  useEffect(() => {
    if (!userLocation) return;

    const defaultZoom = userLocation.region === 'New Mexico' ? 9.5 : 10;

    setTimeout(() => {
      if (!hasRezoomed) {
        setZoom(defaultZoom);
        setHasRezoomed(true);
      }
    }, 0);

    setCenter({
      lat: parseFloat(userLocation.latitude),
      lng: parseFloat(userLocation.longitude),
    });
  }, [userLocation, zoom, hasRezoomed]);

  const { chargingStations, fetchChargingStations } = useChargingStations();
  const [selectedStation, selectStation, deselectStations] =
    useSelectedStation(chargingStations);
  const visibleChargingStations = filterWithinBounds(chargingStations, bounds);
  const filteredChargingStations = ignoreSuperchargerStations
    ? visibleChargingStations.filter(
        (station) => !isSuperChargingStation(station)
      )
    : visibleChargingStations;

  const powerFilteredChargingStations =
    userPrefs.get('vehicleTypeFilter') === 'All'
      ? filteredChargingStations
      : filteredChargingStations.filter((station) =>
          userPrefs.get('vehicleTypeFilter') === 'highPower'
            ? Number(station.ev_dc_fast_num)
            : userPrefs.get('vehicleTypeFilter') === 'public'
            ? station.access_code === 'public'
            : station.access_code !== 'public'
        );

  const { clusteredPoints, individualPoints, supercluster } = useMappedClusters(
    {
      data: powerFilteredChargingStations,
      bounds,
      zoom,
    }
  );

  const onMapChange = ({
    center: newCenter,
    bounds: newBounds,
    zoom: newZoom,
  }) => {
    setBounds(newBounds);
    additionalBounds(newBounds);
    setZoom(newZoom);
    const { lat: latitude, lng: longitude } = newCenter;

    fetchChargingStations({ latitude, longitude }, newBounds);
    additionalFetch({ latitude, longitude }, newBounds);
  };

  const saveMapRefs = ({ map }) => {
    mapRef.current = map;
    mapRef.current.setOptions({
      disableDefaultUI: true,
      fullscreenControl: false,
      zoomControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      cameraControl: false,
    });
  };

  const setMapZoom = (delta) => {
    if (!mapRef.current || typeof mapRef.current.getZoom !== 'function') return;

    const currentZoom = Number(mapRef.current.getZoom()) || zoom;
    const nextZoom = Math.max(2, Math.min(20, currentZoom + delta));

    mapRef.current.setZoom(nextZoom);
  };

  const panMap = (direction) => {
    if (!mapRef.current || typeof mapRef.current.panBy !== 'function') return;

    const panStep = 120;
    const directionOffsets = {
      up: [0, -panStep],
      down: [0, panStep],
      left: [-panStep, 0],
      right: [panStep, 0],
    };

    const [xOffset, yOffset] = directionOffsets[direction] || [0, 0];
    mapRef.current.panBy(xOffset, yOffset);
  };

  const toggleFullscreen = () => {
    const mapContainer = mapContainerRef.current;
    if (!mapContainer || isTogglingFullscreen.current) return;

    isTogglingFullscreen.current = true;

    if (document.fullscreenElement) {
      document.exitFullscreen().finally(() => {
        isTogglingFullscreen.current = false;
      });
      return;
    }

    if (mapContainer.requestFullscreen) {
      mapContainer.requestFullscreen().finally(() => {
        isTogglingFullscreen.current = false;
      });
    } else {
      isTogglingFullscreen.current = false;
    }
  };
  return (
    <div className="ChargingMap">
      <div className="row hide-offscreen">
        <div className="col-sm-12 text-center">
          <h2>
            <FormattedMessage
              id="chargingStations"
              defaultMessage="Charging Stations"
              description="Charging Stations"
            />
          </h2>
        </div>
      </div>
      <div className="row input-working-zipcode">
        <InputWorkingZipcodeForChargingMap zipcode={DEFAULT_MAP_ZIPCODE} />
      </div>
      <div className="row map-wrapper">
        <div className="map-container-wrapper">
          {!userLocation && (
            <div className="spinner-container">
              <LoadingSpinner />
            </div>
          )}
          {userLocation && (
            <>
              <div className="map-container" ref={mapContainerRef}>
                <div
                  className="custom-map-controls custom-map-controls-top-right"
                  aria-label="Map fullscreen control"
                >
                  <button
                    className="custom-map-control-button custom-map-control-button-fullscreen"
                    onClick={toggleFullscreen}
                    type="button"
                    aria-label="Toggle fullscreen"
                    title="Toggle fullscreen"
                  >
                    <img
                      src={fullscreenIcon}
                      alt=""
                      className="custom-map-control-icon-fullscreen"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div
                  className="custom-map-controls custom-map-controls-bottom-right"
                  aria-label="Map controls"
                >
                  <button
                    className="custom-map-control-button custom-map-control-button-toggle"
                    onClick={() =>
                      setShowMapControls((currentValue) => !currentValue)
                    }
                    type="button"
                    aria-label="Toggle map controls"
                    aria-expanded={showMapControls}
                    aria-controls="custom-map-controls-panel"
                    title="Toggle map controls"
                  >
                    <img
                      src={mapControlsToggleIcon}
                      alt=""
                      className="custom-map-control-icon-toggle"
                      aria-hidden="true"
                    />
                  </button>
                  {showMapControls && (
                    <div
                      id="custom-map-controls-panel"
                      className="custom-map-controls-panel"
                    >
                      <div className="custom-map-pan-controls" aria-label="Pan controls">
                        <button
                          className="custom-map-control-button custom-map-control-button-pan"
                          onClick={() => panMap('up')}
                          type="button"
                          aria-label="Pan up"
                          title="Pan up"
                        >
                          &#8593;
                        </button>
                        <div className="custom-map-pan-row">
                          <button
                            className="custom-map-control-button custom-map-control-button-pan"
                            onClick={() => panMap('left')}
                            type="button"
                            aria-label="Pan left"
                            title="Pan left"
                          >
                            &#8592;
                          </button>
                          <button
                            className="custom-map-control-button custom-map-control-button-pan"
                            onClick={() => panMap('right')}
                            type="button"
                            aria-label="Pan right"
                            title="Pan right"
                          >
                            &#8594;
                          </button>
                        </div>
                        <button
                          className="custom-map-control-button custom-map-control-button-pan"
                          onClick={() => panMap('down')}
                          type="button"
                          aria-label="Pan down"
                          title="Pan down"
                        >
                          &#8595;
                        </button>
                      </div>
                      <button
                        className="custom-map-control-button"
                        onClick={() => setMapZoom(1)}
                        type="button"
                        aria-label="Zoom in"
                        title="Zoom in"
                      >
                        +
                      </button>
                      <button
                        className="custom-map-control-button"
                        onClick={() => setMapZoom(-1)}
                        type="button"
                        aria-label="Zoom out"
                        title="Zoom out"
                      >
                        &#8722;
                      </button>
                    </div>
                  )}
                </div>
                {isVisible && (
                  <BaseGoogleMapReact
                    center={center}
                    zoom={zoom}
                    disableDefaultControls
                    onChange={onMapChange}
                    onGoogleApiLoaded={saveMapRefs}
                  >
                    {individualPoints.map((point) => (
                      <ChargingStationsMapMarker
                        key={point.properties.data.id}
                        station={point.properties.data}
                        lat={point.geometry.coordinates[1]}
                        lng={point.geometry.coordinates[0]}
                        onMouseOver={() => {
                          selectStation(point.properties.data.id);
                        }}
                        onClick={deselectStations}
                        isSelected={
                          point.properties.data.id === selectedStation?.id
                        }
                      />
                    ))}
                    {clusteredPoints.map((cluster) => (
                      <ChargingStationClusterMarker
                        key={cluster.id}
                        cluster={cluster}
                        supercluster={supercluster}
                        map={mapRef.current}
                        lat={cluster.geometry.coordinates[1]}
                        lng={cluster.geometry.coordinates[0]}
                      />
                    ))}
                  </BaseGoogleMapReact>
                )}
              </div>
              <div className="disclaimer-container">
                <p className="charging-map-legal-disclaimer legal-disclaimer">
                  <FormattedMessage
                    id="disclaimer.chargingMap"
                    defaultMessage="Mileage ranges are based upon estimates provided by EV manufacturers. Your range may vary based upon climate, region, traffic, car model, and automobile conditions. Data from the Alternative Fuel Data Center may not reflect latest availability. Please confirm availability of charging stations before commencing your journey. Submit suspected missing charging stations {link}."
                    values={{
                      link: (
                        <a
                          href="https://afdc.energy.gov/stations/#/station/new"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          here
                        </a>
                      ),
                    }}
                  />
                </p>
              </div>
            </>
          )}
        </div>
        <div
          className="map-control-wrapper"
          style={{ width: 'auto', padding: '0 0 0 38px' }}
        >
          <MapControlPanel
            selectedStation={selectedStation}
            chargingStations={powerFilteredChargingStations}
            toggleWithHistory={toggleWithHistory}
          ></MapControlPanel>
        </div>
      </div>
    </div>
  );
};

export default ChargingMap;

ChargingMap.propTypes = {
  userLocation: PropTypes.object,
  ignoreSuperchargerStations: PropTypes.bool,
};
